import { FC, MouseEvent } from 'react';
import { SegmentFieldBaseProps } from './helpers';
import { Button, Stack, Theme } from '@mui/material';
import { createUseStyles } from 'react-jss';
import Text from '@/components/text';
import Icon from '@/components/icon';
import classNames from 'classnames';

const useStyles = createUseStyles((theme: Theme) => ({
  root: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
    minWidth: 0,
    height: 28,
  },
  rootReadonly: {
    backgroundColor: theme.palette.subtleGray,
  },
  rootSearch: {
    backgroundColor: theme.palette.common.white,
  },
  leftSegment: {
    flexDirection: 'row',
    backgroundColor: theme.palette.lightGray,
    alignItems: 'center',
    gap: 4,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  leftSegmentActive: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  rightSegment: {
    alignItems: 'center',
    minWidth: 0,
    flex: 1,
  },
  rightSegmentReadonly: {
    padding: '4px 8px',
  },
  rightSegmentButton: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  clearSegment: {
    borderRadius: 0,
    padding: 0,
    minHeight: 28,
    minWidth: 28,
    color: theme.palette.darkGray,
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  clearSegmentIcon: {
    lineHeight: 0,
  },
}));

const SegmentFieldBase: FC<SegmentFieldBaseProps> = ({
  leftLabel,
  variant = 'readonly',
  leftIcon,
  leftActive = false,
  className = '',
  onClick,
  rightContent,
  onClear,
}) => {
  const readonly = variant === 'readonly';
  const hasClick = !!onClick;
  const styles = useStyles();
  const segmentFieldBase = classNames(
    styles.root,
    readonly ? styles.rootReadonly : styles.rootSearch,
    className
  );
  const leftSegment = classNames(styles.leftSegment, leftActive && styles.leftSegmentActive);
  const rightSegment = classNames(
    styles.rightSegment,
    readonly && styles.rightSegmentReadonly,
    hasClick && styles.rightSegmentButton
  );
  const handleClearClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onClear?.();
  };
  const handleLeftSegmentClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <Stack direction="row" className={segmentFieldBase}>
      <Stack className={leftSegment} onClick={handleLeftSegmentClick} px={1}>
        {leftIcon && <Icon name={leftIcon} size="small" color={leftActive ? 'white' : undefined} />}
        <Text size="small" bold color={leftActive ? 'white' : 'black'}>
          {leftLabel}
        </Text>
      </Stack>
      <Stack
        className={rightSegment}
        role={hasClick ? 'button' : undefined}
        tabIndex={hasClick ? 0 : undefined}
        onClick={onClick}
      >
        {rightContent}
      </Stack>
      {onClear && (
        <Button
          onClick={handleClearClick}
          title={`Clear ${leftLabel} filter`}
          size="small"
          className={styles.clearSegment}
          type="button"
        >
          <Icon name="close" size="x-small" className={styles.clearSegmentIcon} />
        </Button>
      )}
    </Stack>
  );
};

export default SegmentFieldBase;
